/**
 * Game Player Dispatcher
 * Routes to the correct game player based on module gameType.
 */

import { t } from '../i18n/config.js';

/**
 * Render game player interface.
 * Dispatches to the appropriate game player based on gameType,
 * or renders a default placeholder if gameType is unrecognized.
 * @param {Object} profile - Child profile with id, name
 * @param {Object} module - Module data with id, title, categoryImage, gameConfig
 */
export async function renderGamePlayer(profile, module) {
  const contentContainer = document.getElementById('content-container');

  if (!contentContainer) {
    console.error('content-container not found');
    return;
  }

  const gameType = module?.gameConfig?.gameType;

  switch (gameType) {
    case 'memory': {
      const { renderMemoryGamePlayer } = await import('./MemoryGamePlayer.js');
      return renderMemoryGamePlayer(profile, module);
    }
    default:
      renderPlaceholder(contentContainer, profile, module);
  }
}

/**
 * Render the default placeholder game player UI.
 * Used when gameType is not recognized or not configured.
 * @param {HTMLElement} contentContainer - The container element
 * @param {Object} profile - Child profile
 * @param {Object} module - Module data
 */
function renderPlaceholder(contentContainer, profile, module) {
  contentContainer.innerHTML = `
    <div class="game-player-container">
      <!-- Game Header -->
      <header class="game-header">
        <button class="custom-button" id="backBtn">
          <div class="bottom-div pinkBG"></div>
          <div class="top-div kassieBG">
            <img class="icon-placeholder" src="/svg/icons/LeftArrow.svg" alt="" width="20" height="20" />
            <span class="button-text">${t('child.navigation.back')}</span>
          </div>
        </button>

        <div class="game-info">
          <h1>${module.title}</h1>
          <p>${t('child.navigation.games')}</p>
        </div>

        <div class="game-score">
          <img src="/svg/components/StarSingle.svg" alt="Telling" width="24">
          <span>0</span>
        </div>
      </header>

      <!-- Game Content -->
      <main class="game-content">
        <div class="game-placeholder">
          <img src="/svg/icons/GameController.svg" alt="${t('child.navigation.games')}" width="120">
          <h2>${t('child.game.gamePlayer')}</h2>
          <p>${t('child.game.placeholder')}</p>
          <p>${t('child.game.fullImplementation')}</p>

          <button class="start-game-btn">
            <img src="/svg/media/GreenPlayCircle.svg" alt="${t('child.actions.startGame')}" width="32">
            ${t('child.actions.startGame')}
          </button>
        </div>
      </main>
    </div>
  `;

  // Bind placeholder events
  bindPlaceholderEvents(profile);
}

/**
 * Bind placeholder game player events
 * @param {Object} profile - Child profile
 */
function bindPlaceholderEvents(profile) {
  const backBtn = document.getElementById('backBtn');
  const startGameBtn = document.querySelector('.start-game-btn');

  // Back button - navigate back to library
  backBtn.addEventListener('click', async () => {
    const { router } = await import('../utils/router.js');
    await router.navigate('library', profile);
  });

  // Start game button
  startGameBtn.addEventListener('click', () => {
    import('../utils/toast.js').then(({ showToast }) => {
      showToast(t('child.game.gameWillCome'), 'info');
    });
  });
}
